/*
 */

import type { PartnershipResult } from '../context/partnerships-wizard-types';

/** A geographic cluster of partner-search results with its label + count. */
export interface ResultCluster {
  key: string;
  label: string;
  count: number;
  results: PartnershipResult[];
}

/** Counts of results per canonical advertiser bucket, for the summary row. */
export interface AdvertiserBreakdown {
  owner: number;
  agency: number;
  broker: number;
  unknown: number;
}

const ADVERTISER_KEYS: Array<keyof AdvertiserBreakdown> = [
  'owner',
  'agency',
  'broker',
  'unknown',
];

/**
 * Group results into geographic clusters (neighbourhood first, then city,
 * then a shared "Outros" bucket), preserving first-seen order both across and
 * within clusters so the listing is stable and reads like the search's own
 * geography rather than a flat dump.
 *
 * @param results - The partner-search results to cluster.
 * @param fallbackLabel - Label for results with no neighbourhood/city.
 * @returns The ordered clusters, each with its label and count.
 */
export function clusterResultsByGeo(
  results: PartnershipResult[],
  fallbackLabel: string,
): ResultCluster[] {
  const order: string[] = [];
  const byKey = new Map<string, ResultCluster>();
  for (const result of results) {
    const label = geoLabel(result, fallbackLabel);
    const key = label.toLowerCase();
    let cluster = byKey.get(key);
    if (!cluster) {
      cluster = { key, label, count: 0, results: [] };
      byKey.set(key, cluster);
      order.push(key);
    }
    cluster.results.push(result);
    cluster.count += 1;
  }
  return order.map((key) => byKey.get(key)!);
}

/**
 * Tally how many results fall into each canonical advertiser bucket. An
 * unrecognised or absent advertiser_type counts as "unknown" so the breakdown
 * always sums to the result total.
 *
 * @param results - The partner-search results to tally.
 * @returns The per-bucket counts.
 */
export function advertiserBreakdown(
  results: PartnershipResult[],
): AdvertiserBreakdown {
  const breakdown: AdvertiserBreakdown = {
    owner: 0,
    agency: 0,
    broker: 0,
    unknown: 0,
  };
  for (const result of results) {
    const bucket = normalizeBucket(result.advertiser_type);
    breakdown[bucket] += 1;
  }
  return breakdown;
}

/**
 * The advertiser buckets present in a breakdown, in canonical order, as
 * `[bucket, count]` pairs — ready for the summary chips (empty buckets are
 * omitted so the row only shows what was actually found).
 *
 * @param breakdown - The per-bucket counts.
 * @returns The non-empty buckets in canonical order.
 */
export function presentAdvertiserBuckets(
  breakdown: AdvertiserBreakdown,
): Array<[keyof AdvertiserBreakdown, number]> {
  return ADVERTISER_KEYS.filter((k) => breakdown[k] > 0).map(
    (k) => [k, breakdown[k]] as [keyof AdvertiserBreakdown, number],
  );
}

/** The "neighbourhood, city" label of a result, or whichever part exists, or the fallback. */
function geoLabel(result: PartnershipResult, fallback: string): string {
  const neighbourhood = result.neighbourhood?.trim();
  const city = result.city?.trim();
  if (neighbourhood && city) return `${neighbourhood}, ${city}`;
  return neighbourhood || city || fallback;
}

/** Maps an advertiser type onto its breakdown bucket, 'unknown' for anything else. */
function normalizeBucket(
  advertiserType: string | null,
): keyof AdvertiserBreakdown {
  const value = (advertiserType ?? '').trim().toLowerCase();
  if (value === 'owner' || value === 'agency' || value === 'broker') {
    return value;
  }
  return 'unknown';
}
